<template>
  <div class="my-5" v-if="ready">
    <!-- Payment Required -->
    <div v-if="!completed">
      <h3>Payment Details</h3>
      <div class="row">
        <div class="form-group col-12 col-lg-6 col-xl-7">

          <div class="form-group">
            <label for="email">Email</label>
            <input type="email" class="form-control" id="email" v-model="email">
            <ul class="list-unstyled text-danger" v-if="userError && userError.email">
              <li v-for="(description, index) in userError.email" :key="index">{{description}}</li>
            </ul>
          </div>

          <div class="form-group">
            <label for="cardholder">Cardholder Name</label>
            <input type="text" class="form-control" id="cardholder" v-model="name">
            <ul class="list-unstyled text-danger" v-if="userError && userError.name">
              <li v-for="(description, index) in userError.name" :key="index">{{description}}</li>
            </ul>
          </div>

          <payment-form ref="payment"/>

          <div class="row mt-3 mx-1 text-danger" v-if="error">
            <p><icon icon="exclamation-circle" class="text-danger"/> &nbsp;{{error}}</p>
          </div>

          <div class="row mt-3 mx-1">
            <button class="btn btn-primary" @click="payAndSignUp" :disabled="loading"><icon icon="cog" spin v-if="loading"/> Pay and Sign up</button>
          </div>
          <div class="row mt-2 mx-1">
            <p class="card-text mb-3 text-muted">Your payment information is securely handled by our payment provider <a href="https://www.stripe.com" target="_blank">Stripe</a></p>.
          </div>
        </div>
        <div class="col-12 col-lg-6 col-xl-5">
          <quote-receipt :quote="quote" :transaction="transaction" />
        </div>
      </div>
    </div>
    <!-- Payment Completed -->
    <div v-else>
      <h1 class="text-center"><icon icon="check-circle" class="text-success"/></h1>
      <h1 class="text-center mb-4 mx-5">Invoice protected</h1>
      <p class="text-center mb-2 mx-5">Your invoice is now protected under the policy <span class="font-weight-bold text-nowrap">{{policy.number}}</span></p>
      <p class="text-center mb-4 mx-5">Your payment receipt of <b>{{quote.total_price | currency}}</b> is available in your inbox</p>

      <!-- explanatory cards -->
      <div class="card-deck mt-5">
        <!-- second card -->
        <form @submit.prevent="submitPassword" class="card shadow border-0 py-2" v-if="needsPassword">
          <div class="card-body pb-0">
            <div class="card-block text-muted px-4 pt-2">
              <h2 class="card-title">Choose a password</h2>
              <p>We've already created a Hokodo account for you to keep track of your insured invoices. All you need to do is choose a password.</p>
            </div>
          </div>
          <div class="card-body text-muted px-5">
            <vue-password
              id="inputPassword"
              classes="input form-control"
              v-model="password"
              placeholder="Enter your password"
              disable-strength/>
              <ul class="text-danger list-unstyled" v-if="passwordError && passwordError.password">
                <li v-for="(description, index) in passwordError.password" :key="index">{{description}}</li>
              </ul>
              <ul class="card-text mt-2">
                <li>Minimum 8 characters in length</li>
                <li>Must contain lower case letters, upper case letters, numbers, and special characters.</li>
                <li>Must not contain dictionary words/common names</li>
              </ul>
            <button type="submit" class="mt-3 btn btn-primary">Set password</button>
          </div>
        </form>
      </div>
      <div class="text-center mt-5" v-if="!needsPassword">
        <a class="btn btn-primary" :href="customerPortalURL">Continue to Account</a>
      </div>
    </div>
  </div>
</template>

<script>
import { TransactionAPI } from '@/api/api'

import VuePassword from 'vue-password'
import QuoteReceipt from '@/components/QuoteReceipt.vue'
import PaymentForm from '@/components/PaymentForm.vue'

export default {
  name: 'payment',
  components: {
    VuePassword,
    QuoteReceipt,
    PaymentForm
  },
  data () {
    return {
      customerPortalURL: process.env.VUE_APP_CUSTOMER_PORTAL,
      // Debug
      debug: false,
      debugData: { email: '' },
      // Data
      transaction: null,
      quote: null,
      policy: null,
      // Form fields
      name: null,
      email: null,
      user: null,
      userError: null,
      password: '',
      passwordError: null,
      error: null,
      // Flags
      ready: false,
      loading: false
    }
  },
  computed: {
    needsPassword: function () {
      return this.debug || this.$store.state.user.needsPassword
    },
    completed: function () { return !!this.policy }
  },
  mounted: function () {
    this.api = new TransactionAPI()
    this.initialize()
    // Segment-Track Payment page viewed
    window.analytics.page('Buy First Policy', 'Payment Details')
    // Segment-Track Payment started - Needed for intercom
    window.analytics.track('Buy First Policy - Payment Started')
  },
  methods: {
    handleFailure: function (msg, goToReferrer) {
      console.warn(msg)
      if (goToReferrer) {
        this.$router.replace({ 'name': 'protect' })
      }
    },
    initialize: function () {
      const {
        id: transactionId,
        key: transactionKey
      } = this.$route.query

      if (transactionId && transactionKey) {
        console.debug('Initializing transaction payment', transactionId, transactionKey)
      } else {
        this.handleFailure('Cannot initialize transaction, credentials not set', true)
        return
      }

      this.api.setAuthorization('TransactionKey', transactionKey)
      this.api.getTransaction(transactionId).then(transaction => {
        this.transaction = transaction
      }).then(() => {
        // XXX: is it certain that the attached quote is relevant ?
        if (this.transaction.quote) {
          return Promise.resolve(this.transaction.quote)
        }
        return this.api.getQuote({ transaction: transactionId }).catch(error => {
          if (error.response.status === 400) {
            // XXX: status 400 may not be enough to be sure that
            // the wizard should be reset
            this.handleFailure('Cannot retrieve quote, transaction incomplete', true)
          } else {
            console.log(error)
          }
        })
      }).then(quote => {
        this.quote = quote
      }).then(() => {
        this.ready = true
        if (this.transaction.policy) {
          this.policy = this.transaction.policy
        }
      })
    },
    getOrCreateUser: function () {
      return new Promise((resolve, reject) => {
        if (this.user) {
          resolve(this.user)
        } else {
          // Try to create the user
          this.$store.dispatch('user/create', {
            email: this.email,
            name: this.name
          }).then((userResponse) => {
            // User is successfully created ? continue
            resolve(userResponse)
          }).catch(error => {
            console.info('user cannot be created', error)
            this.userError = error.response.data
            // TODO : User already exists ? pause and authenticate
            // Something else ? Throw error
            reject(error)
          })
        }
      })
    },
    setTransactionOwner: function (userData) {
      return this.api.setOwner({
        transaction_id: this.transaction.id,
        user_id: userData.id
      }).then(() => {
        // NOTE : the transaction has been patched, so a quote reload is necessary
        this.api.getQuote({ transaction: this.transaction.id })
          .then(quote => {
            this.quote = quote
          })
      })
    },
    handlePayment: function (transactionData, handleError) {
      return this.$refs.payment.proceed({
        owner: { name: this.name, email: this.email }
      }).catch(handleError)
    },
    registerPayment: function (purchaseData, handleError) {
      return this.api.payPolicy({
        quote: this.quote.id,
        stripe_id: purchaseData.source.id
      }).then(policy => {
        this.policy = policy
      })
    },
    finish: function () {
      // Segment Track Signed Up
      window.analytics.track('Signed Up', {
        name: this.name,
        email: this.email
      })
      // Segment Identify Group - Account company
      window.analytics.group({
        name: this.transaction.creditor.name,
        email: this.email
      })
      // Segment Track Signed Up
      window.analytics.track('Buy First Policy - Payment Completed')

      // Segment Identify user
      window.analytics.identify({
        name: this.name,
        email: this.email,
        userId: this.api.setOwner.user_id // FIXME: trying to get user_id
      })
    },
    payAndSignUp: function () {
      this.loading = true
      // First get or create the user

      const handleError = (error) => {
        if (error.response) {
          // The server responded with an error
          if (error.response.status === 400) {
            if ('email' in error.response.data) {
              this.error = 'Please enter a valid email address and try again.'
            } else {
              // Quote has expired. We can potentially also check for {"quote":["Quote is declined, expired or already accepted."]} to be more certain about the specific error
              // TODO: this needs to be handled better - e.g. get a new quote. Leaving as a placeholder until the way forward has been decided
              // TODO: there are other possible causes for a 400 error, such as email address that doesn't look like an email address, when registering the user
              this.error = 'Sorry, your quote has expired. Please go back to the start and try again.'
            }
          } else if (error.response.status === 402) {
            // Payment failed
            this.error = 'Sorry, your payment was rejected. Please check the information you entered, or try another card.'
          } else {
            // Another server error occured
            this.error = 'Sorry, something went wrong while we were processing your payment. Please try again, or contact us at support@hokodo.co.'
          }
        } else {
          // The request was made but no response was received or something else happened in setting up the request that triggered an error
          this.error = 'Sorry, something went wrong while we were processing your payment. Please check the information you entered and try again, or contact us at support@hokodo.co.'
        }
      }

      this.getOrCreateUser()
        // Once user is known, set transaction ownership
        .then(this.setTransactionOwner)
        // Once the user and transaction are set, create payment source
        .then(this.handlePayment)
        // Finally, record the purchase
        .then(this.registerPayment)
        .then(this.finish)
        .catch(handleError)
        .finally(() => { this.loading = false })
    },
    submitPassword () {
      let payload = { password: this.password }
      if (this.debug) {
        Object.assign(payload, this.debugData)
      }
      this.$store.dispatch('user/setPassword', payload).then(() => {
        // Redirect user to a confirm email page
        this.$router.push('confirm-email')
      }).catch(error => {
        // FIXME: response should be unpacked in API connector
        this.passwordError = error.response.data
      })
    }

  }
}
</script>
